import {
  integrationConfig,
  isBackendConfigured,
  stripeCheckoutOrigin,
} from './integrationConfig'
import type {
  MembershipPlanId,
  PractitionerAccountInput,
  PractitionerApiErrorCode,
  PractitionerSession,
} from '../types/practitionerSignup'

/**
 * Browser → Supabase integration for the web practitioner signup flow.
 *
 * Architecture:
 *   Website ──► Edge Function `create-practitioner-account` ──► Supabase Auth
 *   Website ──► Supabase Auth password grant (public anon key) ──► session JWT
 *   Website ──► Edge Function `create-practitioner-checkout` (Bearer JWT)
 *            ──► Stripe Checkout Session ──► redirect to hosted Checkout
 *   Stripe  ──► webhook Edge Function ──► practitioner_subscriptions
 *
 * SECURITY
 * - Passwords are sent over HTTPS directly to Supabase and are never logged,
 *   persisted, or included in analytics. Do not add console logging here.
 * - The user ID is NOT sent in request bodies. The checkout Edge Function must
 *   derive it from the verified JWT in the Authorization header.
 * - A redirect to /practitioners/welcome is NOT proof of payment. Subscription
 *   status is written server-side by the Stripe webhook only.
 */

export class PractitionerApiError extends Error {
  code: PractitionerApiErrorCode

  constructor(code: PractitionerApiErrorCode, message: string) {
    super(message)
    this.name = 'PractitionerApiError'
    this.code = code
  }
}

function assertConfigured() {
  if (!isBackendConfigured()) {
    // TODO(dev): supply VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.
    throw new PractitionerApiError(
      'not_configured',
      'Practitioner signup is not connected yet.',
    )
  }
}

function publicHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    apikey: integrationConfig.supabaseAnonKey,
  }
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>
  } catch {
    return {}
  }
}

async function request(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch {
    throw new PractitionerApiError(
      'network',
      'We couldn’t reach Fem Entity. Check your connection and try again.',
    )
  }
}

function serverMessage(body: Record<string, unknown>): string | undefined {
  const candidate = body.error ?? body.message ?? body.msg ?? body.error_description
  return typeof candidate === 'string' ? candidate : undefined
}

/**
 * INTEGRATION POINT — Supabase Edge Function: create practitioner account.
 *
 * TODO(dev): implement Edge Function `create-practitioner-account`:
 *   - Accept { first_name, last_name, email, password, terms_accepted }.
 *   - Reject if terms_accepted !== true.
 *   - Create the Auth user server-side with the service role key, e.g.
 *       auth.admin.createUser({ email, password, email_confirm: <per policy>,
 *         user_metadata: { first_name, last_name },
 *         app_metadata:  { role: 'practitioner' } })
 *     Set role in app_metadata (or your profiles table) — NOT user_metadata,
 *     which clients can edit.
 *   - Create the practitioner profile row with onboarding = not completed so
 *     the mobile app routes the user to onboarding Step 1.
 *   - Record terms acceptance (timestamp + policy version).
 *   - Respond 201 on success, 409 if the email already exists.
 *   - Never log the password.
 */
export async function createPractitionerAccount(
  input: PractitionerAccountInput,
): Promise<void> {
  assertConfigured()

  const response = await request(
    `${integrationConfig.supabaseUrl}/functions/v1/${integrationConfig.createAccountFunction}`,
    {
      method: 'POST',
      headers: {
        ...publicHeaders(),
        Authorization: `Bearer ${integrationConfig.supabaseAnonKey}`,
      },
      body: JSON.stringify(input),
    },
  )

  if (response.ok) return

  const body = await readJson(response)
  if (response.status === 409) {
    throw new PractitionerApiError(
      'email_exists',
      'An account with this email already exists.',
    )
  }
  throw new PractitionerApiError(
    'server',
    serverMessage(body) ?? 'We couldn’t create your account. Please try again.',
  )
}

/**
 * INTEGRATION POINT — Supabase Auth password sign-in (GoTrue REST).
 * Uses only the public anon key. Returns a session held in memory by the caller.
 *
 * TODO(dev): if you add @supabase/supabase-js to the site, this can be replaced
 * with supabase.auth.signInWithPassword(). If you do, configure the client with
 * `persistSession: false` or accept Supabase's standard session storage.
 */
export async function signInPractitioner(
  email: string,
  password: string,
): Promise<PractitionerSession> {
  assertConfigured()

  const response = await request(
    `${integrationConfig.supabaseUrl}/auth/v1/token?grant_type=password`,
    {
      method: 'POST',
      headers: publicHeaders(),
      body: JSON.stringify({
        email,
        password,
      }),
    },
  )

  const body = await readJson(response)

  if (!response.ok) {
    const code = typeof body.error_code === 'string' ? body.error_code : ''
    if (code === 'email_not_confirmed') {
      throw new PractitionerApiError(
        'email_not_confirmed',
        'Please confirm your email address, then sign in to continue.',
      )
    }
    if (response.status === 400 || code === 'invalid_credentials') {
      throw new PractitionerApiError(
        'invalid_credentials',
        'That email and password don’t match our records.',
      )
    }
    throw new PractitionerApiError(
      'server',
      serverMessage(body) ?? 'We couldn’t sign you in. Please try again.',
    )
  }

  const user = (body.user ?? {}) as {
    id?: string
    email?: string
  }
  const accessToken = typeof body.access_token === 'string' ? body.access_token : ''
  const expiresIn = typeof body.expires_in === 'number' ? body.expires_in : 3600

  if (!accessToken || !user.id) {
    throw new PractitionerApiError(
      'server',
      'We couldn’t start your session. Please try again.',
    )
  }

  return {
    accessToken,
    userId: user.id,
    email: user.email ?? email,
    expiresAt: Date.now() + expiresIn * 1000,
  }
}

/**
 * INTEGRATION POINT — Supabase Edge Function: create Stripe Checkout Session.
 *
 * TODO(dev): implement Edge Function `create-practitioner-checkout`:
 *   - Verify the Bearer JWT; derive user_id from it (never from the body).
 *   - Confirm the user's role is 'practitioner'.
 *   - Map plan_id → Stripe Price ID server-side (anchor | bloom | thrive | collective).
 *     Collective = $274.99 base + $24.99/seat — model per your Stripe setup.
 *   - Apply the Founding Member offer (e.g. subscription_data.trial_period_days
 *     covering the two complimentary months) server-side.
 *   - Create a subscription-mode Checkout Session with:
 *       client_reference_id = user_id, metadata = { user_id, plan_id }
 *       success_url = `${SITE_URL}/practitioners/welcome?session_id={CHECKOUT_SESSION_ID}`
 *       cancel_url  = `${SITE_URL}/practitioners/membership?checkout=cancelled`
 *     Build these URLs server-side from a SITE_URL secret (not from the client).
 *   - Respond { url: session.url }.
 *   The Stripe webhook function then writes practitioner_subscriptions.
 */
export async function startPractitionerCheckout(
  planId: MembershipPlanId,
  session: PractitionerSession,
): Promise<string> {
  assertConfigured()

  const response = await request(
    `${integrationConfig.supabaseUrl}/functions/v1/${integrationConfig.checkoutFunction}`,
    {
      method: 'POST',
      headers: {
        ...publicHeaders(),
        Authorization: `Bearer ${session.accessToken}`,
      },
      body: JSON.stringify({
        plan_id: planId,
      }),
    },
  )

  const body = await readJson(response)

  if (!response.ok) {
    throw new PractitionerApiError(
      'server',
      serverMessage(body) ?? 'We couldn’t start checkout. Please try again.',
    )
  }

  const url = typeof body.url === 'string' ? body.url : ''
  if (!url.startsWith(`${stripeCheckoutOrigin}/`)) {
    throw new PractitionerApiError(
      'invalid_checkout_url',
      'We couldn’t start secure checkout. Please try again.',
    )
  }
  return url
}

export function describeError(error: unknown): string {
  if (error instanceof PractitionerApiError) return error.message
  return 'Something went wrong. Please try again.'
}
